import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { useAuth } from '../../AuthContext'
import { richUiComponentLabel, richUiComponentOrder } from './richUiComponents'

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const TH = 'text-secondary border-b border-secondary px-2.5 py-1.5 text-left text-[11px] font-semibold whitespace-nowrap'
const TD = 'text-primary px-2.5 py-1.5 align-middle'

export default function AIPurchaseAssistDashboard() {
  const { siteId } = useParams()
  const { authHeaders } = useAuth()
  const [days, setDays] = useState(7)
  const [data, setData] = useState(null)
  const [extData, setExtData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [r, r2] = await Promise.all([
        fetch(`/api/sites/${siteId}/purchase-assist/dashboard?days=${days}`, { headers: authHeaders() }),
        fetch(`/api/sites/${siteId}/purchase-assist/dashboard-extended?days=${days}`, { headers: authHeaders() }),
      ])
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      setData(await r.json())
      if (r2.ok) setExtData(await r2.json())
      setError(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { fetchAll() }, [siteId, days])

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='📊 Findeeダッシュボード'
        description='Findeeの効果指標。会話数・経由CVR・GMV・LLMコスト・推薦商品ランキングを確認できます。'
        rightNode={
          <div className='flex flex-wrap items-center gap-1.5'>
            <span className='text-secondary text-[11px]'>期間:</span>
            {[7, 30, 90].map(d => {
              const on = days === d
              return (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  className={`cursor-pointer rounded-md px-3 py-1 text-[11px] font-semibold ${on ? 'bg-fill-brand text-on-bg-fill' : 'bg-surface-secondary text-secondary hover:bg-surface-hover'}`}
                >過去{d}日</button>
              )
            })}
            <Button color={ButtonColor.SECONDARY} onClick={fetchAll}>🔄 更新</Button>
          </div>
        }
      />

      {error && (
        <div className='bg-surface-critical text-critical mb-2.5 rounded-lg p-2.5 text-xs'>エラー: {error}</div>
      )}

      {loading || !data ? (
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      ) : (
        <>
          {/* KPIカード4種 */}
          <div className='mb-4 grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3'>
            <KpiCard
              title='🗣 利用状況'
              icon='💬'
              primary={`${data.current.conversations.toLocaleString()} 会話`}
              secondary={`UU ${data.current.unique_visitors.toLocaleString()}`}
              prevValue={data.previous.conversations}
              currentValue={data.current.conversations}
            />
            <KpiCard
              title='💰 成果'
              icon='💸'
              primary={data.current.has_order_data
                ? `経由CVR ${data.current.cvr_usage.toFixed(2)}%`
                : '未計測'}
              secondary={data.current.has_order_data
                ? `GMV ¥${data.current.gmv.toLocaleString()} / 推薦経由CVR ${data.current.cvr_recommended.toFixed(2)}% / クリック経由CVR ${data.current.cvr_via_click.toFixed(2)}%`
                : '注文完了ページに計測タグが未設置です'}
              prevValue={data.previous.cvr_usage}
              currentValue={data.current.cvr_usage}
              isPercent
            />
            <KpiCard
              title='⭐ 品質'
              icon='👍'
              primary={`満足度 ${data.current.satisfaction_rate.toFixed(1)}%`}
              secondary={`👍 ${data.current.thumbs_up} / 👎 ${data.current.thumbs_down}`}
              prevValue={data.previous.satisfaction_rate}
              currentValue={data.current.satisfaction_rate}
              isPercent
            />
            <KpiCard
              title='⚠ アラート'
              icon='🚨'
              primary={`問題 ${data.current.problem_count} 件`}
              secondary={`コスト ¥${data.current.cost_jpy.toFixed(0)}`}
              prevValue={data.previous.problem_count}
              currentValue={data.current.problem_count}
              reverseColor
            />
          </div>

          {/* 日次推移 */}
          <Section title='📈 日次推移'>
            {data.daily_series.every(s => !s.conversations && !s.gmv) ? (
              <div className='text-tertiary py-8 text-center text-xs'>期間内にデータがありません</div>
            ) : (
              <DailyChart series={data.daily_series} />
            )}
          </Section>

          {/* 経由vs未経由比較 + 頻出質問 + 「分かりません」率 */}
          {extData && extData.comparison && extData.comparison.via_assist && extData.comparison.non_assist && (
            <div className='mb-3.5 grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-3.5'>
              {/* Findee 経由 vs 未経由 */}
              <div className={`${CARD} p-3.5`}>
                <h3 className='text-primary m-0 mb-2.5 text-[13px] font-bold'>
                  ⚖️ Findee 経由 vs 未経由 比較
                </h3>
                <div className='overflow-x-auto'>
                  <table className='w-full border-collapse text-[11px]'>
                    <thead>
                      <tr className='bg-surface-secondary'>
                        <th className={TH}></th>
                        <th className={`${TH} text-success text-right`}>Findee 経由</th>
                        <th className={`${TH} text-right`}>未経由</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className='border-b border-secondary'>
                        <td className={TD}>会話数</td>
                        <td className={`${TD} text-right`}>{extData.comparison.via_assist.conversations.toLocaleString()}</td>
                        <td className={`${TD} text-tertiary text-right`}>-</td>
                      </tr>
                      <tr className='border-b border-secondary'>
                        <td className={TD}>セッション数</td>
                        <td className={`${TD} text-right`}>
                          <NonAssistValue
                            available={extData.comparison.data_source?.has_session_data}
                            value={extData.comparison.via_assist.sessions?.toLocaleString()}
                          />
                        </td>
                        <td className={`${TD} text-secondary text-right`}>
                          <NonAssistValue
                            available={extData.comparison.data_source?.has_session_data}
                            value={extData.comparison.non_assist.sessions?.toLocaleString()}
                          />
                        </td>
                      </tr>
                      <tr className='border-b border-secondary'>
                        <td className={TD}>購買数</td>
                        <td className={`${TD} text-right`}>{extData.comparison.via_assist.purchase_count.toLocaleString()}</td>
                        <td className={`${TD} text-secondary text-right`}>
                          <NonAssistValue
                            available={extData.comparison.data_source?.has_order_data}
                            value={extData.comparison.non_assist.purchase_count.toLocaleString()}
                          />
                        </td>
                      </tr>
                      <tr className='border-b border-secondary'>
                        <td className={TD}>CVR</td>
                        <td className={`${TD} text-success text-right font-bold`}>{extData.comparison.via_assist.cvr.toFixed(2)}%</td>
                        <td className={`${TD} text-secondary text-right`}>
                          <NonAssistValue
                            available={extData.comparison.non_assist.cvr !== null && extData.comparison.non_assist.cvr !== undefined}
                            value={`${(extData.comparison.non_assist.cvr ?? 0).toFixed(2)}%`}
                          />
                        </td>
                      </tr>
                      <tr className='border-b border-secondary'>
                        <td className={TD}>GMV</td>
                        <td className={`${TD} text-right`}>¥{extData.comparison.via_assist.gmv.toLocaleString()}</td>
                        <td className={`${TD} text-secondary text-right`}>
                          <NonAssistValue
                            available={extData.comparison.data_source?.has_order_data}
                            value={`¥${extData.comparison.non_assist.gmv.toLocaleString()}`}
                          />
                        </td>
                      </tr>
                      <tr className='border-b border-secondary'>
                        <td className={TD}>AOV</td>
                        <td className={`${TD} text-right`}>¥{extData.comparison.via_assist.aov.toLocaleString()}</td>
                        <td className={`${TD} text-secondary text-right`}>
                          <NonAssistValue
                            available={extData.comparison.data_source?.has_order_data}
                            value={`¥${extData.comparison.non_assist.aov.toLocaleString()}`}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className='text-tertiary mt-1.5 text-[10px] leading-relaxed'>
                  ※ 経由 = 会話と<strong>確定突合</strong>できた受注のみ。突合は顧客IDまたは訪問者IDの一致で行い、
                  時間の近さによる推定はしません。突合できなかった受注は未経由に入ります。<br />
                  そのため Cookie 削除・別端末での購入は未経由に落ち、<strong>経由CVRは下振れします</strong>（過大評価を避けるため）。<br />
                  参照データ：Findee 専用の <span className='font-mono'>purchase_assist_orders</span> /
                  <span className='font-mono'> purchase_assist_sessions</span>（AIレコメンドの受注・行動データとは分離）。
                  {routeLabel(extData.comparison.data_source?.match_routes) && (
                    <><br />突合の内訳：{routeLabel(extData.comparison.data_source.match_routes)}</>
                  )}
                  {(!extData.comparison.data_source?.has_order_data || !extData.comparison.data_source?.has_session_data) && (
                    <>
                      <br />
                      <span className='text-warning'>
                        未計測：
                        {!extData.comparison.data_source?.has_order_data && '受注'}
                        {!extData.comparison.data_source?.has_order_data && !extData.comparison.data_source?.has_session_data && '・'}
                        {!extData.comparison.data_source?.has_session_data && 'セッション'}
                        が期間内に0件です。注文完了ページに計測タグを設置してください（配信設定 &gt; 埋め込みコード）。
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* 頻出質問TOP10 + 「分かりません」率 */}
              <div className={`${CARD} p-3.5`}>
                <h3 className='text-primary m-0 mb-2.5 text-[13px] font-bold'>
                  ❓ 頻出質問TOP10
                </h3>
                {extData.top_questions.length === 0 ? (
                  <div className='text-tertiary py-4 text-center text-[11px]'>データなし</div>
                ) : (
                  <ol className='text-secondary m-0 list-decimal pl-4 text-[11px] leading-relaxed'>
                    {extData.top_questions.map((q, i) => (
                      <li key={i} className='mb-1'>
                        <span className='text-primary'>{q.question}</span>
                        <span className='text-tertiary ml-1.5 text-[10px]'>×{q.count}</span>
                      </li>
                    ))}
                  </ol>
                )}
                <div className='bg-surface-warning mt-3 rounded border border-secondary p-2'>
                  <div className='text-secondary mb-0.5 text-[10px]'>「分かりません」率</div>
                  <div className={`text-sm font-bold ${extData.cant_answer_rate > 10 ? 'text-critical' : 'text-warning'}`}>
                    {extData.cant_answer_rate.toFixed(2)}%
                    <span className='text-tertiary ml-1.5 text-[10px] font-normal'>
                      ({extData.cant_answer_count} / {extData.total_assistant_messages} 回答)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 推薦商品TOP10 */}
          <Section title='🏆 推薦商品TOP10（参照回数順）'>
            {data.top_recommended_products.length === 0 ? (
              <div className='text-tertiary py-4 text-center text-xs'>データがありません</div>
            ) : (
              <div className='overflow-x-auto'>
                <table className='w-full border-collapse text-xs'>
                  <thead>
                    <tr className='bg-surface-secondary'>
                      <th className={TH}>順位</th>
                      <th className={TH}>商品ID</th>
                      <th className={TH}>商品名</th>
                      <th className={TH}>画像</th>
                      <th className={`${TH} text-right`}>参照回数</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_recommended_products.map((p, i) => (
                      <TopProductRow key={p.entity_id ?? i} rank={i + 1} product={p} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* LLMコスト */}
          <Section title='💸 LLMコスト内訳'>
            <div className='mb-3 grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-2'>
              <Stat label='総呼び出し' value={data.llm_cost.total_calls.toLocaleString()} />
              <Stat label='総コスト' value={`¥${data.llm_cost.total_cost_jpy.toFixed(0)}`} />
              <Stat label='1回あたり' value={`¥${data.llm_cost.avg_cost_jpy_per_call.toFixed(2)}`} />
              <Stat label='キャッシュHit率' value={`${data.llm_cost.cache_hit_rate.toFixed(1)}%`} />
            </div>
            {data.llm_cost.breakdown.length > 0 && (
              <div className='overflow-x-auto'>
                <table className='w-full border-collapse text-[11px]'>
                  <thead>
                    <tr className='bg-surface-secondary'>
                      <th className={TH}>モデル</th>
                      <th className={TH}>段階</th>
                      <th className={`${TH} text-right`}>呼出数</th>
                      <th className={`${TH} text-right`}>入力Token</th>
                      <th className={`${TH} text-right`}>出力Token</th>
                      <th className={`${TH} text-right`}>コスト</th>
                      <th className={`${TH} text-right`}>キャッシュHit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.llm_cost.breakdown.map((b, i) => (
                      <tr key={i} className='border-b border-secondary'>
                        <td className={`${TD} text-secondary font-mono text-[10px]`}>{b.model}</td>
                        <td className={`${TD} text-emphasis`}>{b.stage}</td>
                        <td className={`${TD} text-right`}>{b.calls.toLocaleString()}</td>
                        <td className={`${TD} text-secondary text-right`}>{b.input_tokens.toLocaleString()}</td>
                        <td className={`${TD} text-secondary text-right`}>{b.output_tokens.toLocaleString()}</td>
                        <td className={`${TD} text-success text-right`}>¥{b.cost_jpy.toFixed(2)}</td>
                        <td className={`${TD} text-warning text-right`}>{b.cache_hits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <ComponentAnalyticsSection siteId={siteId} authHeaders={authHeaders} days={days} />
        </>
      )}
    </Main>
  )
}

// 未経由側のセル。元データが無いときに 0 を出すと「0件だった」と誤読されるため、
// 算出不能であることを明示する。
function NonAssistValue({ available, value }) {
  if (!available) return <span className='text-tertiary'>未計測</span>
  return <>{value}</>
}

const MATCH_ROUTE_LABELS = { customer_id: '顧客ID', visitor_id: '訪問者ID' }

// 突合経路の内訳を「顧客ID 3件 / 訪問者ID 1件」の形にする
function routeLabel(routes) {
  if (!routes) return ''
  const parts = Object.entries(routes)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${MATCH_ROUTE_LABELS[k] || k} ${n}件`)
  return parts.join(' / ')
}

// 推薦商品TOP10の1行。画像はリンクを押したときだけ読み込む（一覧表示のたびに
// 商品画像を10件取りに行かないようにするため）。展開時の高さは3行ぶん相当。
const PRODUCT_IMAGE_HEIGHT = 84

function TopProductRow({ rank, product }) {
  const [showImage, setShowImage] = useState(false)
  const [imageError, setImageError] = useState(false)
  const hasImage = Boolean(product.image_url)

  return (
    <tr className='border-b border-secondary'>
      <td className={`${TD} text-warning font-bold`}>{rank}</td>
      <td className={`${TD} font-mono`}>#{product.entity_id}</td>
      <td className={TD}>
        {product.product_name ? (
          product.product_url ? (
            <a
              href={product.product_url}
              target='_blank'
              rel='noopener noreferrer'
              className='text-emphasis hover:underline'
            >{product.product_name}</a>
          ) : (
            <span>{product.product_name}</span>
          )
        ) : (
          <span className='text-tertiary'>（商品マスタに該当なし）</span>
        )}
      </td>
      <td className={TD}>
        {!hasImage ? (
          <span className='text-tertiary text-[10px]'>-</span>
        ) : (
          <div className='flex flex-col items-start gap-1'>
            <button
              type='button'
              onClick={() => setShowImage(v => !v)}
              className='text-emphasis cursor-pointer text-[11px] hover:underline'
            >{showImage ? '画像を閉じる' : '画像を表示'}</button>
            {showImage && (
              imageError ? (
                <span className='text-tertiary text-[10px]'>画像を読み込めませんでした</span>
              ) : (
                <img
                  src={product.image_url}
                  alt={product.product_name || product.entity_id}
                  onError={() => setImageError(true)}
                  className='rounded border border-secondary object-contain'
                  style={{ height: PRODUCT_IMAGE_HEIGHT, maxWidth: PRODUCT_IMAGE_HEIGHT * 2 }}
                />
              )
            )}
          </div>
        )}
      </td>
      <td className={`${TD} text-success text-right font-bold`}>{product.reference_count}</td>
    </tr>
  )
}

function ComponentAnalyticsSection({ siteId, authHeaders, days }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let live = true
    setLoading(true)
    fetch(`/api/sites/${siteId}/purchase-assist/components/analytics?days=${days}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : { items: [] })
      .then(d => {
        if (!live) return
        // リッチUIデザイナーのナビ順に揃えて表示する
        const list = [...(d.items || [])].sort(
          (a, b) => richUiComponentOrder(a.component_type) - richUiComponentOrder(b.component_type)
        )
        setItems(list)
        setLoading(false)
      })
      .catch(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [siteId, days])
  return (
    <div className={`${CARD} mt-4 p-4`}>
      <div className='text-primary mb-2.5 text-[13px] font-bold'>
        🎨 リッチUIコンポーネント別 効果指標
      </div>
      {loading ? <div className='text-secondary text-xs'>読み込み中...</div>
        : items.length === 0 ? <div className='text-secondary text-xs'>まだイベントが記録されていません</div>
        : (
          <div className='overflow-x-auto'>
            <table className='w-full border-collapse text-xs'>
              <thead>
                <tr className='bg-surface-secondary'>
                  <th className={TH}>コンポーネント</th>
                  <th className={`${TH} text-right`}>表示</th>
                  <th className={`${TH} text-right`}>インタラクション</th>
                  <th className={`${TH} text-right`}>CTR</th>
                  <th className={`${TH} text-right`}>コンバージョン</th>
                  <th className={`${TH} text-right`}>CVR</th>
                </tr>
              </thead>
              <tbody>
                {items.map(i => (
                  <tr key={i.component_type} className='border-b border-secondary'>
                    <td className={TD}>
                      {richUiComponentLabel(i.component_type)}
                      <span className='text-tertiary ml-1.5 font-mono text-[10px]'>{i.component_type}</span>
                    </td>
                    <td className={`${TD} text-right`}>{i.view}</td>
                    <td className={`${TD} text-right`}>{i.interaction}</td>
                    <td className={`${TD} text-success text-right`}>{i.ctr}%</td>
                    <td className={`${TD} text-right`}>{i.conversion}</td>
                    <td className={`${TD} text-warning text-right`}>{i.cvr}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* 2026-10-03 修正：CTR の定義を明示（SMART_HUB_DEV-137） */}
            <div className='text-tertiary mt-2 text-[10px]'>
              表示・CTR は応答単位で数えます（CTR＝表示された応答のうち、操作された応答の割合）。インタラクションは操作の延べ回数です。
            </div>
          </div>
        )}
    </div>
  )
}

function KpiCard({ title, icon, primary, secondary, prevValue, currentValue, reverseColor, isPercent }) {
  const diff = (prevValue && prevValue > 0) ? ((currentValue - prevValue) / prevValue * 100) : null
  const isImprovement = reverseColor ? (diff < 0) : (diff > 0)
  const diffColor = diff === null ? 'text-tertiary' : (isImprovement ? 'text-success' : 'text-critical')
  return (
    <div className={`${CARD} p-3.5`} style={{ borderLeft: '4px solid var(--color-fill-brand)' }}>
      <div className='text-secondary mb-1 text-[11px]'>{title}</div>
      <div className='text-primary mb-0.5 text-lg font-bold'>{icon} {primary}</div>
      <div className='text-tertiary text-[10px]'>{secondary}</div>
      {diff !== null && (
        <div className={`mt-1.5 text-[10px] ${diffColor}`}>
          前期間比: {diff > 0 ? '+' : ''}{diff.toFixed(1)}%
        </div>
      )}
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div className={`${CARD} mb-3.5 p-3.5`}>
      <h3 className='text-primary m-0 mb-2.5 text-[13px] font-bold'>{title}</h3>
      {children}
    </div>
  )
}

function Stat({ label, value }) {
  return (
    <div className='bg-surface-secondary rounded-lg p-2.5'>
      <div className='text-secondary text-[10px]'>{label}</div>
      <div className='text-primary mt-0.5 text-base font-bold'>{value}</div>
    </div>
  )
}

function DailyChart({ series }) {
  const maxConv = Math.max(...series.map(s => s.conversations), 1)
  const maxGmv = Math.max(...series.map(s => s.gmv), 1)
  // 会話も購買も無い日は棒を描かない（1pxの棒が並ぶと「少しあった日」と誤読されるため）
  return (
    <div>
      <div className='flex gap-2'>
        {/* 左軸（会話数の最大値） */}
        <div className='text-tertiary flex h-36 w-8 shrink-0 flex-col justify-between text-right text-[9px]'>
          <span>{maxConv.toLocaleString()}</span>
          <span>0</span>
        </div>
        <div className='mb-1.5 flex h-36 flex-1 items-end gap-1 border-b border-l border-secondary pl-1'>
          {series.map((s, i) => {
            const h1 = (s.conversations / maxConv) * 100
            const h2 = (s.gmv / maxGmv) * 100
            return (
              // 高さ%が効くよう、日ごとの列にも h-full を持たせて親（h-36）まで高さを伝播させる
              <div
                key={i}
                className='flex h-full flex-1 items-end justify-center gap-px'
                title={`${s.date}\n会話 ${s.conversations} / 購買 ${s.purchases} / GMV ¥${s.gmv.toLocaleString()}`}
              >
                {/* 期間が短いときに棒が細くなりすぎないよう、幅は列幅に追従させる（上限あり） */}
                <div className='bg-fill-brand min-w-[3px] flex-1 rounded-t-sm' style={{ height: `${h1}%`, maxWidth: 18, minHeight: s.conversations > 0 ? 2 : 0 }} />
                <div className='bg-fill-success min-w-[3px] flex-1 rounded-t-sm' style={{ height: `${h2}%`, maxWidth: 18, minHeight: s.gmv > 0 ? 2 : 0 }} />
              </div>
            )
          })}
        </div>
        {/* 右軸（GMVの最大値） */}
        <div className='text-tertiary flex h-36 w-14 shrink-0 flex-col justify-between text-[9px]'>
          <span>¥{maxGmv.toLocaleString()}</span>
          <span>¥0</span>
        </div>
      </div>
      <div className='text-tertiary flex justify-between pl-10 pr-14 text-[9px]'>
        <span>{series[0]?.date}</span>
        <span>{series[series.length - 1]?.date}</span>
      </div>
      <div className='text-secondary mt-2 flex gap-3 text-[10px]'>
        <span><span className='bg-fill-brand mr-1 inline-block h-2.5 w-2.5' />会話数（左軸）</span>
        <span><span className='bg-fill-success mr-1 inline-block h-2.5 w-2.5' />GMV（右軸）</span>
      </div>
    </div>
  )
}
